import { jsx as _jsx, Fragment as _Fragment, jsxs as _jsxs } from "react/jsx-runtime";
import { useAemConfig } from '@as-react/common-components';
import { CountryId, ErrorMessage, } from '@as-react/common-types';
import { ButtonAlpha, StackBlock, isPossiblePhoneNumber, Divider, TextAlpha, Banner, CheckoutLayout, Grid, } from '@yonderland/andes-react';
import { yupResolver } from '@hookform/resolvers/yup';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useForm, FormProvider } from 'react-hook-form';
import * as Yup from 'yup';
import dayjs from 'dayjs';
import { I18N_NAMESPACES } from '../../../constants/i18nConstants';
import { TitleField } from '../../form/registration-fields/TitleField';
import { useCheckoutConfig } from '../../../context/CheckoutContext';
import customParseFormat from 'dayjs/plugin/customParseFormat';
import isBetween from 'dayjs/plugin/isBetween';
import { InvoiceFields } from '../../form/registration-fields/InvoiceFields';
import { CustomerDetailsFields } from '../../form/registration-fields/CustomerDetailsFields';
import { AddressFields } from '../../form/registration-fields/AddressFields';
import { getCountryByFictiveWebShop } from '../../../util/CountryUtil';
import { NewsLetterField } from '../../form/registration-fields/NewsLetterField';
import { CountryChangeWatcher } from '../../form/registration-fields/watchers/CountryChangeWatcher';
import { emptyAddressFields, getAddressSearchValidationSchema, getAddressValidationSchema, } from '../../form/address-fields/addressFields.utils';
import { getInvoiceValidationSchema } from '../../form/invoice-fields/invoiceFields.utils';
dayjs.extend(customParseFormat);
dayjs.extend(isBetween);
export const PERSONAL_DETAILS_FORM_NAME = 'personal-details-form';
export const PersonalDetailsForm = ({ onSubmit, errorFeedback, isLoading, }) => {
    const { t } = useTranslation([
        I18N_NAMESPACES.account,
        I18N_NAMESPACES.checkout,
        I18N_NAMESPACES.general,
        I18N_NAMESPACES.navigation,
    ]);
    const { market, fictiveWebShop, lang } = useAemConfig();
    const defaultCountryCode = getCountryByFictiveWebShop(fictiveWebShop);
    const [activeCountry, setActiveCountry] = useState();
    const showMiddleName = market === 'nl' || (activeCountry === null || activeCountry === void 0 ? void 0 : activeCountry.idCountry) === CountryId.NL;
    const { registrationForm: { showInvoiceCheckbox, showAddress, showNewsletterCheckbox, sectionTitle, showDepartmentName, showDateOfBirth, showMobilePhoneNumber, dateOfBirthExplanationText, }, } = useCheckoutConfig();
    const validationSchema = Yup.object({
        title: Yup.number().required(t(ErrorMessage.ACCOUNT_REQUIRED)),
        gender: Yup.string().required(t(ErrorMessage.ACCOUNT_REQUIRED)),
        firstName: Yup.string().required(t(ErrorMessage.ACCOUNT_REQUIRED)),
        lastName: Yup.string().required(t(ErrorMessage.ACCOUNT_REQUIRED)),
        middleName: Yup.string(),
        mobilePhoneNumber: Yup.string().test('phoneNumber', t(ErrorMessage.INVALID_PHONE), val => !val || isPossiblePhoneNumber(val)),
        dateOfBirth: Yup.string().test('checkDateOfBirth', t(ErrorMessage.INVALID_DATE), (val = '') => {
            if (!val)
                return true;
            const isDateValid = dayjs(val, 'DD/MM/YYYY', true).isValid();
            if (!isDateValid)
                return false;
            return dayjs(val, 'DD/MM/YYYY').isBetween(dayjs().subtract(100, 'year'), dayjs().subtract(11, 'year'), 'year');
        }),
        invoice: Yup.boolean(),
        finance: Yup.object().when('invoice', {
            is: true,
            then: () => getInvoiceValidationSchema({
                t,
                country: activeCountry === null || activeCountry === void 0 ? void 0 : activeCountry.countryCode,
                fullVatFormEnabled: true,
                withAddress: false,
            }),
        }),
        address: Yup.object().when('invoice', {
            is: true,
            then: () => getAddressValidationSchema({ country: activeCountry === null || activeCountry === void 0 ? void 0 : activeCountry.countryCode, t, isOptional: false }),
            otherwise: () => getAddressValidationSchema({ country: activeCountry === null || activeCountry === void 0 ? void 0 : activeCountry.countryCode, t, isOptional: true }),
        }),
        addressSearch: Yup.object().when('invoice', {
            is: true,
            then: () => getAddressSearchValidationSchema({ country: activeCountry === null || activeCountry === void 0 ? void 0 : activeCountry.countryCode, t, isOptional: false }),
            otherwise: () => getAddressSearchValidationSchema({ country: activeCountry === null || activeCountry === void 0 ? void 0 : activeCountry.countryCode, t, isOptional: true }),
        }),
        newsletter: Yup.boolean(),
    });
    const formProps = useForm({
        defaultValues: {
            title: undefined,
            gender: '',
            invoice: false,
            firstName: '',
            lastName: '',
            finance: {
                vatNumber: '',
                peppolID: '',
                companyName: '',
                departmentName: '',
            },
            middleName: '',
            dateOfBirth: '',
            mobilePhoneNumber: '',
            newsletter: false,
            address: Object.assign(Object.assign({}, emptyAddressFields), { finder: '', idCountry: CountryId[defaultCountryCode.toUpperCase()] }),
            addressSearch: {
                postCode: '',
                houseNumber: '',
            },
        },
        mode: 'onTouched',
        resolver: yupResolver(validationSchema),
    });
    const handleSubmit = values => {
        const country = (activeCountry === null || activeCountry === void 0 ? void 0 : activeCountry.countryDescriptionTranslations[lang.toUpperCase()]) || '';
        onSubmit(Object.assign(Object.assign({}, values), { address: Object.assign(Object.assign({}, values.address), { country }) }));
    };
    return (_jsx(FormProvider, Object.assign({}, formProps, { children: _jsx("form", { name: PERSONAL_DETAILS_FORM_NAME, noValidate: true, children: _jsxs(StackBlock, { gap: "6", children: [sectionTitle && (_jsx(TextAlpha, { variant: "headingS", color: "emphasised", children: sectionTitle })), errorFeedback && _jsx(Banner, { text: errorFeedback, status: "error" }), _jsx(TitleField, {}), _jsx(CustomerDetailsFields, { showDateOfBirth: showDateOfBirth, showMobilePhoneNumber: showMobilePhoneNumber, dateOfBirthExplanationText: dateOfBirthExplanationText, showMiddleName: showMiddleName }), showAddress && (_jsxs(_Fragment, { children: [_jsx(Divider, {}), _jsx(AddressFields, { country: activeCountry === null || activeCountry === void 0 ? void 0 : activeCountry.countryCode, onCountryChange: setActiveCountry })] })), showInvoiceCheckbox && (_jsxs(_Fragment, { children: [_jsx(Divider, {}), _jsx(InvoiceFields, { showAddress: showAddress, showDepartmentName: showDepartmentName, country: activeCountry === null || activeCountry === void 0 ? void 0 : activeCountry.countryCode, onCountryChange: setActiveCountry })] })), showNewsletterCheckbox && (_jsxs(_Fragment, { children: [_jsx(Divider, {}), _jsx(NewsLetterField, {})] })), (activeCountry === null || activeCountry === void 0 ? void 0 : activeCountry.countryCode) && (_jsx(CountryChangeWatcher, { countryIdFieldName: "address.idCountry", fieldPrefix: "address.", vatNumberFieldName: "finance.vatNumber" })), _jsx(CheckoutLayout.Sticky, { children: _jsx(Grid, { columns: 12, children: _jsx(Grid.Cell, { span: { xs: 12, lg: 4 }, offset: { lg: 9 }, children: _jsx(ButtonAlpha, { type: "submit", theme: "branded", state: isLoading ? 'loading' : 'interactive', text: t(`${I18N_NAMESPACES.checkout}:checkout.personal.details.submit`), fullWidth: true, onClick: formProps.handleSubmit(handleSubmit), testId: "personal-details-form-submit" }) }) }) })] }) }) })));
};
